import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiSend, FiInbox, FiUser, FiClock, FiCheck, FiHome } from 'react-icons/fi';
import { MobileAlumniNav, MobileAlumniBottomNav } from './components/AlumniMobileComponents';
import { PATHS } from './utils/platformPaths';

const AlumniMessages = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('received');
  const [messages, setMessages] = useState({ sent: [], received: [] });
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showComposeModal, setShowComposeModal] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate(PATHS.login);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate(PATHS.login);
          return;
        }
        throw new Error(`Failed to fetch messages: ${response.status}`);
      }

      const data = await response.json();
      setMessages(data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      await fetch(`${backendUrl}/api/alumni/messages/${messageId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Update local state
      setMessages(prev => ({
        ...prev,
        received: prev.received.map(msg => 
          msg.id === messageId ? { ...msg, read_at: new Date().toISOString() } : msg
        )
      }));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    if (!message.is_sent && !message.read_at) {
      markAsRead(message.id);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  const unreadCount = messages.received.filter(msg => !msg.read_at).length;

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Navigation */}
      <MobileAlumniNav 
        title="Messages" 
        onMenuClick={() => navigate(PATHS.dashboard)}
      />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20 md:pb-8">
      {/* Header - Desktop */}
      <div className="hidden md:flex mb-8 items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Messages</h1>
          <p className="text-gray-600 mt-1">Connect with fellow alumni</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(PATHS.dashboard)}
            className="inline-flex items-center px-4 py-2 border border-orange-300 rounded-md text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
          >
            <FiHome className="h-4 w-4 mr-2" />
            Dashboard
          </button>
          <button
            onClick={() => setShowComposeModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FiSend className="h-4 w-4 mr-2" />
            Compose
          </button>
        </div>
      </div>

      {/* Header - Mobile */}
      <div className="md:hidden mb-6">
        <div className="text-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
          <p className="text-gray-600 mt-1">Connect with fellow alumni</p>
        </div>
        <div className="flex justify-center space-x-3">
          <button
            onClick={() => navigate(PATHS.dashboard)}
            className="inline-flex items-center px-4 py-2 border border-orange-300 rounded-md text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
          >
            <FiHome className="h-4 w-4 mr-2" />
            Dashboard
          </button>
          <button
            onClick={() => setShowComposeModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FiSend className="h-4 w-4 mr-2" />
            Compose
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('received')}
              className={`py-4 px-6 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'received'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FiInbox className="h-4 w-4 mr-2" />
              Inbox
              {unreadCount > 0 && (
                <span className="ml-2 bg-red-100 text-red-600 text-xs font-medium px-2 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('sent')}
              className={`py-4 px-6 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'sent'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FiSend className="h-4 w-4 mr-2" />
              Sent ({messages.sent.length})
            </button>
          </nav>
        </div>

        {/* Message List */}
        <div className="divide-y divide-gray-200">
          {messages[activeTab].length === 0 ? (
            <div className="text-center py-12">
              <FiMail className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No {activeTab} messages
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {activeTab === 'received' 
                  ? 'You haven\'t received any messages yet.'
                  : 'You haven\'t sent any messages yet.'
                }
              </p>
            </div>
          ) : (
            messages[activeTab].map((message) => (
              <MessageItem
                key={message.id}
                message={message}
                onClick={() => openMessage(message)}
                formatDate={formatDate}
              />
            ))
          )}
        </div>
      </div>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <MessageDetailModal
          message={selectedMessage}
          onClose={() => setSelectedMessage(null)}
          formatDate={formatDate}
        />
      )}

      {/* Compose Modal */}
      {showComposeModal && (
        <ComposeMessageModal
          onClose={() => setShowComposeModal(false)}
          onMessageSent={() => {
            setShowComposeModal(false);
            fetchMessages();
          }}
        />
      )}
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileAlumniBottomNav 
        activeSection="messages"
        onNavigate={(path) => navigate(path)}
      />
    </>
  );
};

const MessageItem = ({ message, onClick, formatDate }) => {
  const isUnread = !message.is_sent && !message.read_at;
  
  return (
    <div
      onClick={onClick}
      className={`p-6 hover:bg-gray-50 cursor-pointer transition-colors ${
        isUnread ? 'bg-blue-50' : ''
      }`}
    >
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          {message.other_user?.headshot_url ? (
            <img
              className="h-10 w-10 rounded-full"
              src={message.other_user.headshot_url}
              alt={`${message.other_user.first_name} ${message.other_user.last_name}`}
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
              <FiUser className="h-5 w-5 text-gray-600" />
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className={`text-sm font-medium text-gray-900 ${isUnread ? 'font-semibold' : ''}`}>
              {message.other_user 
                ? `${message.other_user.first_name} ${message.other_user.last_name}`
                : 'Unknown User'
              }
            </p>
            <div className="flex items-center text-xs text-gray-500">
              <FiClock className="h-3 w-3 mr-1" />
              {formatDate(message.created_at)}
              {isUnread && <div className="ml-2 w-2 h-2 bg-blue-600 rounded-full"></div>}
            </div>
          </div>
          
          <p className={`text-sm mt-1 ${isUnread ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
            {message.subject}
          </p>
          
          <p className="text-sm text-gray-500 mt-1 truncate">
            {message.message.substring(0, 100)}
            {message.message.length > 100 && '...'}
          </p>
        </div>
      </div>
    </div>
  );
};

const MessageDetailModal = ({ message, onClose, formatDate }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-full sm:max-w-2xl mx-2 sm:mx-0 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {message.other_user?.headshot_url ? (
                <img
                  className="h-10 w-10 rounded-full"
                  src={message.other_user.headshot_url}
                  alt={`${message.other_user.first_name} ${message.other_user.last_name}`}
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                  <FiUser className="h-5 w-5 text-gray-600" />
                </div>
              )}
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {message.other_user 
                    ? `${message.other_user.first_name} ${message.other_user.last_name}`
                    : 'Unknown User'
                  }
                </h3>
                <p className="text-sm text-gray-500">{formatDate(message.created_at)}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Subject */}
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{message.subject}</h2>
        </div>
        
        {/* Message Content */}
        <div className="px-3 sm:px-6 py-4 sm:py-6">
          <div className="prose max-w-none text-gray-700">
            {message.message.split('\n').map((paragraph, index) => (
              <p key={index} className="mb-4 whitespace-pre-wrap">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ComposeMessageModal = ({ onClose, onMessageSent }) => {
  const navigate = useNavigate();
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [recipientQuery, setRecipientQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  
  const dropdownRef = useRef(null);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search for alumni users
  const searchAlumni = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate(PATHS.login);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/directory/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate(PATHS.login);
          return;
        }
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();
      setSearchResults(data.alumni || []);
      setShowDropdown(true);
    } catch (error) {
      console.error('Error searching alumni:', error);
    } finally {
      setSearching(false);
    }
  };

  // Handle recipient selection
  const selectRecipient = (alumni) => {
    setSelectedRecipient(alumni);
    setRecipientQuery(`${alumni.first_name} ${alumni.last_name}`);
    setShowDropdown(false);
    setSearchResults([]);
  };

  // Handle input change with debounce
  const handleRecipientInput = (e) => {
    const value = e.target.value;
    setRecipientQuery(value);
    
    // Clear selection if user is typing
    if (selectedRecipient && value !== `${selectedRecipient.first_name} ${selectedRecipient.last_name}`) {
      setSelectedRecipient(null);
    }

    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      searchAlumni(value);
    }, 300);
  };

  const sendMessage = async () => {
    if (!selectedRecipient || !subject.trim() || !message.trim()) {
      setError('Please fill in all fields and select a recipient.');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate(PATHS.login);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_id: selectedRecipient.id,
          subject: subject.trim(),
          message: message.trim()
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate(PATHS.login);
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send message');
      }

      onMessageSent();
    } catch (error) {
      console.error('Message send error:', error);
      setError(error.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-full sm:max-w-2xl mx-2 sm:mx-0">
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-medium text-gray-900">Compose Message</h3>
        </div>
        
        <div className="px-3 sm:px-6 py-3 sm:py-4 space-y-3 sm:space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Send To
            </label>
            <div className="relative">
              <input
                type="text"
                value={recipientQuery}
                onChange={handleRecipientInput}
                onFocus={() => recipientQuery && setShowDropdown(true)}
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Search for alumni by name..."
              />
              {searching && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
              
              {showDropdown && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {searchResults.map((alumni) => (
                    <button
                      key={alumni.id}
                      type="button"
                      onClick={() => selectRecipient(alumni)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-3"
                    >
                      <div className="flex-shrink-0">
                        {alumni.headshot_url ? (
                          <img
                            className="h-8 w-8 rounded-full"
                            src={alumni.headshot_url}
                            alt={`${alumni.first_name} ${alumni.last_name}`}
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                            <FiUser className="h-4 w-4 text-gray-600" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {alumni.first_name} {alumni.last_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {alumni.current_employer && `${alumni.current_employer} • `}
                          {alumni.graduation_year && `Class of ${alumni.graduation_year}`}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {selectedRecipient && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md flex items-center space-x-2">
                <FiCheck className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-800">
                  Selected: {selectedRecipient.first_name} {selectedRecipient.last_name}
                </span>
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Message subject..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Write your message..."
            />
          </div>
        </div>
        
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={onClose}
            disabled={sending}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={sendMessage}
            disabled={sending}
            className="w-full sm:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {sending ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlumniMessages;